// Reine Normalisierungs-Helfer für die Antworten des KI-Backends.
// Bewusst frei von fetch/DOM/Supabase, damit sie ohne Netzwerk testbar sind.

// Die kanonischen RAB-Felder in genau der Reihenfolge, in der sie am Ende
// aufgefüllt werden. Ein Eintrag ohne Wert bekommt immer den Leerstring.
const RAB_FIELDS = [
  'component',
  'category',
  'activity',
  'hazards',
  'measures',
  'frequency',
  'regulations',
] as const;

// Das KI-Backend und die Modellvarianten liefern deutsche wie englische
// Feldnamen. Statt sie in einer Bedingungskette zu prüfen, steht die Zuordnung
// hier als Tabelle: Alias (kleingeschrieben) -> kanonisches Feld.
const RAB_FIELD_ALIASES: Record<string, typeof RAB_FIELDS[number]> = {
  component: 'component', bauteil: 'component', bezeichnung: 'component', name: 'component',

  category: 'category', kategorie: 'category', gewerk: 'category', bereich: 'category',

  activity: 'activity', taetigkeit: 'activity', 'tätigkeit': 'activity',
  arbeitsschritt: 'activity', arbeitsgang: 'activity',

  hazards: 'hazards', gefaehrdungen: 'hazards', 'gefährdungen': 'hazards',
  gefaehrdung: 'hazards', 'gefährdung': 'hazards', gefahren: 'hazards', gefahr: 'hazards',

  measures: 'measures', massnahmen: 'measures', 'maßnahmen': 'measures',
  massnahme: 'measures', 'maßnahme': 'measures',
  schutzmassnahmen: 'measures', 'schutzmaßnahmen': 'measures',

  frequency: 'frequency', haeufigkeit: 'frequency', 'häufigkeit': 'frequency',
  intervall: 'frequency', 'prüfintervall': 'frequency', pruefintervall: 'frequency',

  regulations: 'regulations', vorschriften: 'regulations', regeln: 'regulations',
  regelwerke: 'regulations', gesetz: 'regulations', regelung: 'regulations',
};

export const normalizeRabEntry = (entry: any): any => {
  if (!entry || typeof entry !== 'object') return entry;
  const normalized: any = {};

  for (const key of Object.keys(entry)) {
    const lower = key.toLowerCase();
    // hasOwn statt direktem Zugriff: sonst würden Schlüssel wie "constructor"
    // oder "toString" über die Prototypkette einen Treffer vortäuschen.
    // Unbekannte Schlüssel bleiben unter ihrem Originalnamen erhalten.
    const canonical = Object.hasOwn(RAB_FIELD_ALIASES, lower) ? RAB_FIELD_ALIASES[lower] : key;
    normalized[canonical] = entry[key];
  }

  for (const field of RAB_FIELDS) {
    normalized[field] = normalized[field] || '';
  }

  return normalized;
};

export const parseAiResponse = (res: any): any => {
  if (!res) return null;

  if (typeof res === 'object') {
    if (res.text !== undefined) {
      return parseAiResponse(res.text);
    }
    if (res.content !== undefined) {
      return parseAiResponse(res.content);
    }
    if (res.data !== undefined) {
      return parseAiResponse(res.data);
    }
  }

  if (typeof res === 'string') {
    const cleaned = res.replace(/```json/g, '').replace(/```/g, '').trim();
    try {
      return parseAiResponse(JSON.parse(cleaned));
    } catch {
      console.warn("Could not parse string response as JSON in geminiService:", res);
      return res;
    }
  }

  return res;
};

export const extractBestArray = (parsed: any): any[] => {
  if (!parsed) return [];
  if (Array.isArray(parsed)) return parsed;

  if (typeof parsed === 'object') {
    if (Array.isArray(parsed.entries)) return parsed.entries;
    if (Array.isArray(parsed.contacts)) return parsed.contacts;
    if (Array.isArray(parsed.services)) return parsed.services;
    if (Array.isArray(parsed.catalog)) return parsed.catalog;
    // LV-Import: das Feld heisst laut Prompt "positions". Ohne diesen Eintrag
    // gewann bei einer Antwort mit mehreren Arrays ({ positions, items })
    // stillschweigend der falsche — im Zweifel ein leeres.
    if (Array.isArray(parsed.positions)) return parsed.positions;
    if (Array.isArray(parsed.positionen)) return parsed.positionen;
    if (Array.isArray(parsed.items)) return parsed.items;
    if (Array.isArray(parsed.results)) return parsed.results;
    if (Array.isArray(parsed.arbeitsschritte)) return parsed.arbeitsschritte;
    if (Array.isArray(parsed.suggestions)) return parsed.suggestions;

    for (const key of Object.keys(parsed)) {
      if (Array.isArray(parsed[key])) {
        return parsed[key];
      }
    }
  }

  return [];
};

// Safety net: the AI is told to omit paragraph/section numbers, but if it still
// returns them (e.g. "ArbSchG § 4 Abs. 2"), strip them here so only the body of
// rules remains ("ArbSchG"). Keeps name-internal numbers like "DGUV Vorschrift
// 38" or "TRBS 2121 Teil 1" intact — only §, Abs., Satz, Nr. and Anhang are removed.
export const stripRegulationParagraphs = (reg: string): string => {
  if (!reg || typeof reg !== 'string') return reg;
  const cleaned = reg
    .split(/[,;]/)
    .map(part => part
      .replace(/§+\s*\d+\s*[a-zA-Z]?/g, '')
      .replace(/\b(Abs\.?|Absatz|Satz|Nr\.?|Nummer)\s*\d+/gi, '')
      .replace(/\b(Anhang)\s+[0-9IVXivx]+/gi, '')
      .replace(/\s{2,}/g, ' ')
      .replace(/[\s\-–,]+$/, '')
      .trim())
    .filter(Boolean)
    .join(', ');
  return cleaned || reg;
};

// Normalisiert eine von Gemini gelieferte Bounding-Box auf das vom Editor erwartete
// Prozent-Format { xmin, xmax, ymin, ymax } im Bereich 0..100 (Ursprung oben links).
// Gemini liefert Boxen je nach Modell als Array [ymin, xmin, ymax, xmax] normalisiert
// auf 0..1000 ODER als Objekt. Diese Funktion fängt beide Fälle robust ab.
export const normalizeBox = (box: any): { xmin: number; xmax: number; ymin: number; ymax: number } => {
  let xmin = 25, xmax = 75, ymin = 25, ymax = 75;

  if (Array.isArray(box) && box.length === 4) {
    // Gemini-Standard: [ymin, xmin, ymax, xmax]
    [ymin, xmin, ymax, xmax] = box.map(Number);
  } else if (box && typeof box === 'object') {
    xmin = Number(box.xmin ?? box.x_min ?? box.left ?? xmin);
    xmax = Number(box.xmax ?? box.x_max ?? box.right ?? xmax);
    ymin = Number(box.ymin ?? box.y_min ?? box.top ?? ymin);
    ymax = Number(box.ymax ?? box.y_max ?? box.bottom ?? ymax);
  }

  // Falls Koordinaten in 0..1000 (Gemini-Norm) statt 0..100 vorliegen → herunterskalieren.
  const maxVal = Math.max(xmin, xmax, ymin, ymax);
  if (maxVal > 100) {
    xmin /= 10; xmax /= 10; ymin /= 10; ymax /= 10;
  }

  // Sicherstellen, dass min < max und alles innerhalb 0..100 liegt.
  const clamp = (v: number) => Math.max(0, Math.min(100, isNaN(v) ? 50 : v));
  let nx1 = clamp(Math.min(xmin, xmax));
  let nx2 = clamp(Math.max(xmin, xmax));
  let ny1 = clamp(Math.min(ymin, ymax));
  let ny2 = clamp(Math.max(ymin, ymax));

  // Mindestgröße, damit der Marker/Crop sinnvoll ist.
  if (nx2 - nx1 < 4) { nx1 = clamp(nx1 - 2); nx2 = clamp(nx2 + 2); }
  if (ny2 - ny1 < 4) { ny1 = clamp(ny1 - 2); ny2 = clamp(ny2 + 2); }

  return { xmin: nx1, xmax: nx2, ymin: ny1, ymax: ny2 };
};
